import React, { useState } from 'react';
import { X, Loader2, RefreshCw } from 'lucide-react';

const PLATFORM_CONFIG = {
  AMAZON: {
    label: 'Refresh Token',
    placeholder: 'Enter new refresh token (Atzr|IwEB...)',
    fieldKey: 'refreshToken',
    color: 'orange',
    textarea: true,
  },
  FLIPKART: {
    label: 'API Key',
    placeholder: 'Enter new Flipkart API Key',
    fieldKey: 'apiKey',
    color: 'blue',
    textarea: false,
  },
  MEESHO: {
    label: 'API Key',
    placeholder: 'Enter new Meesho API Key',
    fieldKey: 'apiKey',
    color: 'purple',
    textarea: false,
  },
};

export default function ReconnectModal({ isOpen, onClose, onReconnect, account }) {
  const [value, setValue] = useState('');
  const [submitting, setSubmitting] = useState(false);

  if (!isOpen || !account) return null;

  const config = PLATFORM_CONFIG[account.platform] || PLATFORM_CONFIG.AMAZON;

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!value.trim()) return;
    setSubmitting(true);
    try {
      await onReconnect({ [config.fieldKey]: value.trim() });
      setValue('');
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center">
      <div className="absolute inset-0 bg-black/50 backdrop-blur-sm" onClick={onClose} />
      <div className="relative bg-white rounded-2xl shadow-2xl w-full max-w-md mx-4 overflow-hidden">
        {/* Header */}
        <div className="flex items-center justify-between px-6 py-4 border-b">
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 rounded-xl bg-[#FFE8C8] flex items-center justify-center">
              <RefreshCw size={18} className="text-[#C45500]" />
            </div>
            <div>
              <h3 className="text-lg font-semibold text-gray-800">Reconnect Account</h3>
              <p className="text-xs text-gray-500">
                {account.platform} - {account.sellerName || account.sellingPartnerId || account.sellerId}
              </p>
            </div>
          </div>
          <button onClick={onClose} className="p-2 hover:bg-gray-100 rounded-lg transition-colors">
            <X size={18} className="text-gray-500" />
          </button>
        </div>

        {/* Body */}
        <form onSubmit={handleSubmit} className="p-6 space-y-5">
          <div className="p-3 bg-gray-50 rounded-lg text-sm text-gray-600">
            <p>Provide a new <strong>{config.label.toLowerCase()}</strong> to re-authorize this account. The existing credentials will be replaced.</p>
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1.5">
              New {config.label} <span className="text-red-500">*</span>
            </label>
            {config.textarea ? (
              <textarea
                value={value}
                onChange={(e) => setValue(e.target.value)}
                placeholder={config.placeholder}
                rows={3}
                required
                className="w-full px-3 py-2.5 border border-gray-300 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-[#f08804]/20 focus:border-[#f08804] resize-none font-mono"
              />
            ) : (
              <input
                type="text"
                value={value}
                onChange={(e) => setValue(e.target.value)}
                placeholder={config.placeholder}
                required
                className="w-full px-3 py-2.5 border border-gray-300 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-[#f08804]/20 focus:border-[#f08804]"
              />
            )}
          </div>

          {/* Footer */}
          <div className="flex items-center justify-end gap-3 pt-2">
            <button
              type="button"
              onClick={onClose}
              className="px-4 py-2.5 text-sm font-medium text-gray-700 bg-gray-100 hover:bg-gray-200 rounded-lg transition-colors"
            >
              Cancel
            </button>
            <button
              type="submit"
              disabled={submitting || !value.trim()}
              className="inline-flex items-center px-5 py-2.5 text-sm font-medium text-white bg-[#232F3E] hover:bg-[#37475A] rounded-lg transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
            >
              {submitting && <Loader2 size={16} className="mr-2 animate-spin" />}
              {submitting ? 'Reconnecting...' : 'Reconnect'}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}
